import { useEffect, useRef, useState } from 'react';
import { partitionFiles, RejectedFile } from '../utils/fileAccept';

/**
 * Window-level file drag/drop.
 *
 * Listens on `window` rather than a container element, so a page gets
 * "drop anywhere" behaviour without restructuring its JSX — pair the returned
 * `isDragging` with `<FileDropOverlay />` for the visual.
 *
 * Only OS file drags are handled: dragging selected text or an image from
 * another tab is ignored and leaves the page alone.
 *
 * Pages with their own attachment lockout (e.g. enviro-deep-research disables
 * attachments for ExxonMobil accounts via `isExxonMobileEmail`) should pass
 * that condition through `disabled`.
 */
export interface UseFileDropOptions {
  /** Called with the files that passed validation. */
  onFiles: (files: File[]) => void;
  /** Called with the files that did not, so the page can surface a message. */
  onReject?: (rejected: RejectedFile[]) => void;
  /** Override the default accepted MIME list from `utils/fileAccept`. */
  mimeTypes?: string[];
  /** Override the default accepted extension list from `utils/fileAccept`. */
  extensions?: string[];
  /** Cap on files per drop. Omit for no limit. */
  maxFiles?: number;
  /**
   * Suppress the overlay and ignore drops, without un-suppressing the
   * browser's default "open the file in this tab" behaviour.
   */
  disabled?: boolean;
}

export interface UseFileDropResult {
  isDragging: boolean;
}

const dragContainsFiles = (dataTransfer: DataTransfer | null): boolean => {
  if (!dataTransfer || !dataTransfer.types) return false;
  // `types` is a DOMStringList in older browsers, so go through Array.from
  // instead of calling `includes` on it directly.
  return Array.from(dataTransfer.types).indexOf('Files') !== -1;
};

export function useFileDrop(options: UseFileDropOptions): UseFileDropResult {
  const { disabled = false } = options;
  const [isDragging, setIsDragging] = useState(false);

  // Hold the latest callbacks and config in a ref so the window listeners are
  // bound exactly once, rather than torn down and rebuilt on every render.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  // dragenter/dragleave fire for every element the cursor crosses, so a plain
  // boolean flickers as the pointer moves over children. Counting enter/leave
  // pairs keeps the overlay steady until the drag truly leaves the window.
  const depthRef = useRef(0);

  // Clear a drag already in progress if the page disables attachments midway
  // (a stream starting, for instance).
  useEffect(() => {
    if (disabled) {
      depthRef.current = 0;
      setIsDragging(false);
    }
  }, [disabled]);

  useEffect(() => {
    const reset = () => {
      depthRef.current = 0;
      setIsDragging(false);
    };

    const handleDragEnter = (e: DragEvent) => {
      if (!dragContainsFiles(e.dataTransfer)) return;
      e.preventDefault();
      if (optionsRef.current.disabled) return;
      depthRef.current += 1;
      setIsDragging(true);
    };

    const handleDragOver = (e: DragEvent) => {
      if (!dragContainsFiles(e.dataTransfer)) return;
      // Without preventDefault here the browser treats the drop as navigation
      // and replaces the page with the file — which would discard the chat.
      // It stays unconditional so that hazard is covered even while disabled.
      e.preventDefault();
      if (e.dataTransfer && !optionsRef.current.disabled) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      if (!dragContainsFiles(e.dataTransfer)) return;
      e.preventDefault();
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setIsDragging(false);
    };

    const handleDrop = (e: DragEvent) => {
      if (!dragContainsFiles(e.dataTransfer)) return;
      e.preventDefault();
      reset();
      if (optionsRef.current.disabled) return;

      const dropped = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
      if (!dropped.length) return;

      const { onFiles, onReject, mimeTypes, extensions, maxFiles } = optionsRef.current;
      const { accepted, rejected } = partitionFiles(dropped, { mimeTypes, extensions, maxFiles });
      if (accepted.length) onFiles(accepted);
      if (rejected.length && onReject) onReject(rejected);
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    window.addEventListener('dragend', reset);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      window.removeEventListener('dragend', reset);
    };
  }, []);

  return { isDragging };
}

export default useFileDrop;
